import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getMeApi, loginApi, registerApi } from "src/utils/apicalls";
import {
  getAuthToken,
  setAuthToken,
  subscribeAuthToken,
} from "src/utils/authToken";
import { setActiveWorkspaceKey } from "src/utils/activeWorkspace";
import {
  AuthSession,
  AuthUser,
  LoginInput,
  RegisterInput,
} from "src/Types/auth.types";

export type AuthStatus = "loading" | "authenticated" | "anonymous";

type AuthContextValue = {
  user: AuthUser | null;
  status: AuthStatus;
  isAdmin: boolean;
  login: (input: LoginInput) => Promise<AuthUser>;
  register: (input: RegisterInput) => Promise<AuthUser>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>(() =>
    getAuthToken() ? "loading" : "anonymous"
  );

  useEffect(
    () =>
      subscribeAuthToken((token) => {
        if (!token) {
          setActiveWorkspaceKey(null);
          setUser(null);
          setStatus("anonymous");
        }
      }),
    []
  );

  useEffect(() => {
    if (!getAuthToken()) return;
    let cancelled = false;
    getMeApi()
      .then((me) => {
        if (cancelled) return;
        setUser(me);
        setStatus("authenticated");
      })
      .catch(() => {
        if (cancelled) return;
        setUser(null);
        setStatus("anonymous");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const applySession = useCallback((session: AuthSession) => {
    setAuthToken(session.token);
    setUser(session.user);
    setStatus("authenticated");
    return session.user;
  }, []);

  const login = useCallback(
    async (input: LoginInput) => applySession(await loginApi(input)),
    [applySession]
  );

  const register = useCallback(
    async (input: RegisterInput) => applySession(await registerApi(input)),
    [applySession]
  );

  const logout = useCallback(() => {
    setAuthToken(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      isAdmin: user?.role === "ADMIN",
      login,
      register,
      logout,
    }),
    [user, status, login, register, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
};
